import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  FileTextIcon,
  GavelIcon,
  MilestoneIcon,
  PaperclipIcon,
  ScrollTextIcon,
  XCircleIcon,
} from "lucide-react";

import type { PreviewFile } from "@/components/files/file-preview-dialog";
import { cn } from "@/lib/utils";

import type { LegalRegistryHistoryEntry } from "../api/use-procedures";
import { toDate, toPreviewFile } from "../lib/procedure-format";

/** Bytes → "1,2 Mo" / "840 Ko", the sizes Box reports for procedure documents. */
function formatFileSize(size: number | null): string | null {
  if (!size) {
    return null;
  }
  if (size < 1024 * 1024) {
    return `${Math.round(size / 1024)} Ko`;
  }
  return `${(size / (1024 * 1024)).toFixed(1).replace(".", ",")} Mo`;
}

function fileExtension(name: string | null): string | null {
  const extension = name?.split(".").pop();
  return extension && extension.length <= 5 ? extension.toUpperCase() : null;
}

type EntryKind = LegalRegistryHistoryEntry["kind"];

/**
 * `kind` is classified by the API so the hover card, this timeline and anything
 * later all read one heuristic — two copies of the same regex drift.
 *
 * `ruling` and `adverse` are what a lawyer scans for; everything else is
 * procedural traffic and is deliberately rendered quieter.
 */
const KIND_STYLES: Record<EntryKind, { node: string; icon: typeof FileTextIcon; decisive: boolean }> = {
  ruling: { node: "bg-violet-100 text-violet-700 ring-violet-600/20", icon: GavelIcon, decisive: true },
  adverse: { node: "bg-red-100 text-red-700 ring-red-600/20", icon: XCircleIcon, decisive: true },
  document: { node: "bg-blue-50 text-blue-600 ring-blue-600/20", icon: FileTextIcon, decisive: false },
  renvoi: { node: "bg-amber-50 text-amber-600 ring-amber-600/20", icon: MilestoneIcon, decisive: false },
  event: { node: "bg-gray-50 text-gray-500 ring-gray-400/25", icon: ScrollTextIcon, decisive: false },
};

/** Shared with the panel banner so a document always looks like a document. */
export function DocumentChip({
  document,
  onPreview,
  className,
}: {
  document: NonNullable<LegalRegistryHistoryEntry["document"]>;
  onPreview: (file: PreviewFile) => void;
  className?: string;
}) {
  // The stored filename is machine-generated noise
  // ("2026001293-Tribunal…-SUBPOENA-2026-01-07T00:00:00.000+00:00-57eea0.pdf"),
  // so the chip shows the document *type* and keeps the filename in the title.
  const meta = [fileExtension(document.fileName), formatFileSize(document.fileSize)]
    .filter(Boolean)
    .join(" · ");

  return (
    <button
      type="button"
      title={document.fileName ?? undefined}
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1 text-left transition-colors hover:border-gray-300 hover:bg-gray-50",
        className,
      )}
      onClick={() => onPreview(toPreviewFile(document))}
    >
      <PaperclipIcon className="h-3 w-3 flex-none text-gray-400" aria-hidden="true" />
      <span className="truncate text-xs font-medium text-gray-900">{document.typeDisplayText}</span>
      {meta ? <span className="flex-none text-[11px] text-gray-500">{meta}</span> : null}
    </button>
  );
}

function TimelineEntry({
  entry,
  isLast,
  showYear,
  onPreview,
}: {
  entry: LegalRegistryHistoryEntry;
  isLast: boolean;
  showYear: boolean;
  onPreview: (file: PreviewFile) => void;
}) {
  const { node, icon: Icon, decisive } = KIND_STYLES[entry.kind];
  const date = toDate(entry.date);
  const title = entry.libelleNature ?? entry.decision ?? "Événement";
  const detail = entry.commentaire && entry.commentaire !== title ? entry.commentaire : null;
  const isUltime = /ultime/i.test(`${entry.decision ?? ""} ${entry.commentaire ?? ""}`);

  return (
    <li className="relative flex gap-3">
      {/* Dates form their own column: aligned and self-grouping, they replace the
          month headers that used to wrap a single entry each. */}
      <div className="w-12 flex-none pt-0.5 text-right">
        <span className="block text-xs font-medium leading-4 tabular-nums text-gray-700">
          {date ? format(date, "d MMM", { locale: fr }).replace(".", "") : "—"}
        </span>
        {showYear && date ? (
          <span className="block text-[11px] leading-4 tabular-nums text-gray-400">
            {format(date, "yyyy")}
          </span>
        ) : null}
      </div>

      <div className="relative flex-none">
        {isLast ? null : (
          <span
            className="absolute left-1/2 top-6 h-[calc(100%-1rem)] w-px -translate-x-1/2 bg-gray-200"
            aria-hidden="true"
          />
        )}
        <span className={cn("relative grid h-6 w-6 place-items-center rounded-full ring-1 ring-inset", node)}>
          <Icon className="h-3 w-3" aria-hidden="true" />
        </span>
      </div>

      <div className={cn("min-w-0 flex-1", isLast ? "pb-0" : "pb-6")}>
        <p
          className={cn(
            "leading-5",
            decisive ? "text-sm font-semibold text-gray-900" : "text-sm font-medium text-gray-700",
          )}
        >
          {title}
        </p>
        {/* Decision and comment are the registry's own words about this step
            ("réponse du demandeur suite notif formulaire C") — boxed together
            and set in a readable grey rather than trailing off as faint lines. */}
        {entry.decision !== title && (entry.decision || detail) ? (
          <div className="mt-1.5 space-y-0.5 rounded-md bg-gray-50 px-2.5 py-1.5 ring-1 ring-inset ring-gray-200">
            {entry.decision && entry.decision !== title ? (
              <p className="text-xs leading-5 font-medium text-gray-800">{entry.decision}</p>
            ) : null}
            {detail ? <p className="text-xs leading-5 text-gray-700">{detail}</p> : null}
          </div>
        ) : null}

        {isUltime ? (
          <span className="mt-1.5 inline-flex items-center rounded-md bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
            Ultime renvoi
          </span>
        ) : null}

        {entry.document ? (
          <DocumentChip className="mt-2" document={entry.document} onPreview={onPreview} />
        ) : null}
      </div>
    </li>
  );
}

/**
 * Case history as an activity feed, newest first. No month headers: with roughly
 * one event per month they wrapped a single line each and doubled the noise —
 * the aligned date column does the grouping instead, and the year only prints
 * when it changes.
 */
export function LegalRegistryTimeline({
  entries,
  onPreview,
}: {
  entries: LegalRegistryHistoryEntry[];
  onPreview: (file: PreviewFile) => void;
}) {
  if (entries.length === 0) {
    return <p className="text-xs text-gray-500">Aucun événement enregistré.</p>;
  }

  const ordered = [...entries].sort(
    (a, b) => (toDate(b.date)?.getTime() ?? 0) - (toDate(a.date)?.getTime() ?? 0),
  );

  return (
    <ol>
      {ordered.map((entry, index) => {
        const year = toDate(entry.date)?.getFullYear();
        const previousYear = index === 0 ? null : toDate(ordered[index - 1].date)?.getFullYear();

        return (
          // Historique lines carry no id and can legitimately repeat (same day,
          // same label), so the index is the only stable key here.
          <TimelineEntry
            key={`${entry.date ?? "no-date"}-${index}`}
            entry={entry}
            isLast={index === ordered.length - 1}
            showYear={index === 0 || year !== previousYear}
            onPreview={onPreview}
          />
        );
      })}
    </ol>
  );
}
